import React, { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useNotifications } from '../../../../../notifications';
import { queriesV2 } from '../../../../../api';
import { uploadDocumentExtraAttachments } from '../../../../../api/mutations';
import { useDocumentState } from '../../../../../contexts/DocumentContext';
import FileUploadModal from '../common/FileUploadModal';
import { BaseUploadProps, UploadFileData } from '../common/types';

const AttachmentUploadContainer: React.FC<BaseUploadProps> = ({
  isOpen,
  onClose,
}) => {
  const [, { error: notifyError }] = useNotifications();
  const { state } = useDocumentState();
  const documentId = state.id;

  const [uploadDocumentExtraAttachmentMutation, { loading }] = useMutation(
    uploadDocumentExtraAttachments,
    {
      refetchQueries: documentId
        ? [
            {
              query: queriesV2.getDocumentExtraAttachments,
              variables: { documentId },
            },
          ]
        : [],
    }
  );

  const handleUpload = useCallback(
    async ({ documentTitle, selectedFile, selectedFiles }: UploadFileData) => {
      try {
        const fileToUpload = selectedFile || (selectedFiles && selectedFiles[0]);
        if (!fileToUpload || !fileToUpload.fileData || !documentId) return;

        const uploadVariables = {
          extraAttachments: [
            {
              file: {
                filename: documentTitle.length > 0 ? documentTitle : fileToUpload.fileName!,
                content: (fileToUpload.fileData as string).split(',')[1],
                contentEncodingType: 'base64',
                contentType: fileToUpload.contentType,
              },
              order: 1,
            },
          ],
          documentId,
        };

        await uploadDocumentExtraAttachmentMutation({
          variables: uploadVariables,
        });

        onClose();
      } catch (error) {
        notifyError({
          message: {
            id: 'attachment-upload-error',
            defaultMessage: `Attachment upload error: ${error}`,
          },
        });
      }
    },
    [uploadDocumentExtraAttachmentMutation, documentId, notifyError, onClose]
  );

  return (
    <FileUploadModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleUpload}
      isLoading={loading}
      templateUpload={false}
      allowMultiple={false}
      modalTitle="Upload Attachment"
      acceptedFileTypes=".pdf"
      showRestrictedEditing={false}
      showDisableWordExport={false}
    />
  );
};

export default AttachmentUploadContainer;